import {
	Button,
	Stack,
	TextField,
	Typography,
	FormControl,
	FormHelperText,
	Box,
	InputLabel,
	Select,
	MenuItem,
	Alert,
	PopperProps
} from '@mui/material';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { ReactComponent as EditIcon } from '../../../assets/PepWise2.0/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/PepWise2.0/delete-img-new.svg';
import { ReactComponent as CloseIcon } from '../../../assets/eva_close-outline_red.svg';
import { ReactComponent as SaveIcon } from '../../../assets/mingcute_check-fill.svg';
import RowTypography from 'components/Table/RowTypography';
import React, { useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import AsyncAutoComplete from 'components/Inputs/ResourceMapping/AsyncAutocomplete';
import { useTheme } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {ReactComponent as WarningImage} from '../../../assets/warning.svg';
import ResourceAllocationSuccessModal from './ResourceAllocationSuccessModal';
import {
	getBulkEditFinopsinputSecEnvOption,
	postResourceAllocationFinops,
	getResourceAllocationFinops
} from 'service/service';
import RowChip from 'components/Table/RowChip';
import LoaderComponent from 'components/Loader';
import ClearAllocationsModal from './ClearAllocationsModal';
import { getProgramProjectHelper } from './ResourceMappingHelper';

type NewAllocationRecords = {
	[key: number]: {};
};

interface Option {
	value: string;
	label: string;
}

export interface TableRow {
	id: number;
	allocId: number;
	isNew: boolean;
	isEditing: boolean;
	PepwiseEnvironment: string;
	PepwiseProject: string;
	PepwiseProgram: string;
	PepwiseSector: string;
	AllocPercent: string;
	startDate: Dayjs;
	endDate: Dayjs | '';
}

interface Allocation {
	pepwiseSector: string;
	pepwiseProject: string;
	pepwiseProgram: string;
	allocation: number;
	projectId: number;
	programId: number;
  }

interface RowData {
	startDate: string;
	endDate: string;
	pepwiseEnvironment: string;
	allocations: Allocation[];
  }

type ModalWidth = "sm" | "md" | "lg" | "xl";
interface ResourceAllocationModalProps {
	currentRow: any;
	allocationType: {
		key: string;
		label: string;
	};
	onCancel: () => void;
	onConfirm: () => void;
	modalTitle: string;
	onTitleChange: (newTitle: string) => void;
    onWidthChange: (newWidth: ModalWidth) => void;
}

const ResourceAllocationModal: React.FC<ResourceAllocationModalProps> = ({ currentRow, onCancel, onConfirm, onTitleChange, onWidthChange, modalTitle, allocationType }) => {
	const theme= useTheme();
	const colors = theme.palette;
	const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
	const validationInitialState = {
		environment: false,
		project: false,
		programs: false,
		sectors: false,
		allocationPercentage: false,
		allocationStartTime: false,
		allocationEndTime: false
	};

	const editErrorMessages = {
		programs:'Program is required',
		project: isDuplicate ? 'Duplicate Program/Project exist within the same timeline' : 'Project is required',
		sectors:'Sector is required',
		allocationPercentage: 'Allocation percentage must be between 0 and 100.',
		allocationStartTime: 'Allocation Start date is required.',
		allocationEndTime: 'Allocation End date is required and must be after start date, and cannot be a future date.'
	};
	

	const [validationErrors, setValidationErrors] = useState(validationInitialState);
	const [editValidationErrors, setEditValidationErrors] = useState(validationInitialState);
	const [singleValidationError, setSingleValidationError] = useState<boolean>(false);
	const [selectedRows, setSelectedRows] = useState<TableRow[]>([]);
	const [originalRows, setOriginalRows] = useState<TableRow[]>([]);
	const [newAllocationRecords, setNewAllocationRecords] = useState<NewAllocationRecords>({});
	const [isSuccesModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
	const [remainingPercentage, ] = useState<number>(0);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [response, setResponse] = useState<any>(null);
	const [errorRes, setErrorRes] = useState<any>(null);

	const [showClearAllocModal, setShowClearAllocModal] = useState<boolean>(false);
	const [isClearAllocation, setIsClearAllocations] = useState<boolean>(false);
	const [groupId, setGroupId] = useState<string>('');
	const [selectedProgramId, setSelectedProgramId] = useState<Record<string,number>>({});
	const [selectedProjectId, setSelectedProjectId] = useState<Record<string,number>>({});
	const [programOptns, setProgramOptns] = useState<Option[]>([]);
	const [projectOptions, setProjectOptions] = useState<Option[]>([]);
	const [sectorOptions, setSectorOptions] = useState<Option[]>([]);
	const [environmentOptions, setEnvironmentOptions] = useState<Option[]>([]);
	const [resource, setResource] = useState<string>('');
	const [currentEditRow, setCurrentEditRow] = useState<TableRow>({
		id: 0,
		allocId: 0,
		isNew: false,
		PepwiseEnvironment: '',
		PepwiseProject: '',
		PepwiseProgram: '',
		PepwiseSector: '',
		AllocPercent: '',
		startDate: dayjs(),
		endDate: '',
		isEditing: true
	});
	
	const isNewAllocRowExists = selectedRows.some((row) => row.isNew || row.isEditing === true);

	const defaultColumnNames = [
		'Projects',
		'Programs',
		'Sectors',
		'Allocation%',
		'Allocation Start Date',
		'Allocation End Date',
		'Actions'
	];

	const columnNames = [
		{ name: 'PepwiseProgram', label: 'Programs', isEditable: true },
		{ name: 'PepwiseProject', label: 'Projects', isEditable: true },
		{ name: 'PepwiseSector', label: 'Sectors', isEditable: true },
		{ name: 'AllocPercent', label: 'Allocation %', isEditable: true },
		{ name: 'startDate', label: 'Allocation Start Date', isEditable: true },
		{ name: 'endDate', label: 'Allocation End Date', isEditable: true },
		{ name: 'actions', label: 'Actions', isEditable: false }
	];

	const [newAllocation, setNewAllocation] = useState<TableRow>({
		id: 0,
		allocId: 0,
		isNew: true,
		PepwiseEnvironment: '',
		PepwiseProject: '',
		PepwiseProgram: '',
		PepwiseSector: '',
		AllocPercent: '',
		startDate: dayjs(),
		endDate: '',
		isEditing: false
	});

	interface FormData {
		groupId: string;
		resource: string;
	}

	const initialFormData: FormData = {
		groupId: '',
		resource: '',
	};
	
	const [formData, setFormData] = useState<FormData>(initialFormData);
	const [environment, setEnvironment] = useState('');
	const [originalEnvironment, setOriginalEnvironment] = useState('');

	const commonInputSx = {
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: '#000000 !important'
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.text.focus} !important`
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.text.focus} !important`
		},
		'& .MuiInputBase-root': {
			minWidth: '12rem',
			padding: '2px',
			fontSize: '12px',
			lineHeight: '0.8',
			height: '33px'
		},
		'& .MuiInputBase-input': {
			padding: '2px',
			fontSize: '12px'
		}
	};

	const commonInputSx2 = {
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: '#000000 !important'
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.text.focus} !important`
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.text.focus} !important`
		},
		'& .MuiInputBase-root': {
			minWidth: '12rem',
			padding: '6px'	
		}
	};

	const commonTextFieldProps = {
		InputLabelProps: {
			sx: {
			'&&': { color: `${colors.textLight.main} !important` },
			'&.Mui-focused': { color: `${colors.textLight.main} !important` },
			'&.MuiInputLabel-shrink': { color: `${colors.textLight.main} !important` },
			},
		},
		InputProps: {
			sx: {
			'&&': { color: `${colors.textLight.main} !important` },
			'& input': { color: `${colors.textLight.main} !important` },
			},
		},
	};

	const commonPopperProps: Partial<PopperProps> = {
		style: {
			maxHeight: '50vh',
			overflowY: 'auto',
		},
	};


	const commonAutocompleteComponentsProps = {
		paper: {
			sx: {
				borderRadius: '4px',
				backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
				color: `${colors.DropdownV2.menuItemText} !important`,

				'&& .MuiAutocomplete-listbox': {
					paddingTop: '0 !important',
					paddingBottom: '0 !important',
				},

				'&& .MuiAutocomplete-option': {
					fontSize: '14px !important',
					fontFamily: 'Montserrat !important',
					color: `${colors.DropdownV2.menuItemText} !important`,
					backgroundColor: 'transparent !important',

					'& .MuiTypography-root': {
						color: `${colors.DropdownV2.menuItemText} !important`,
						fontSize: '14px !important',
						fontFamily: 'Montserrat !important',
						fontWeight: 'inherit !important',
					},
				},

				'&& .MuiAutocomplete-option:hover': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-focused': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-selected': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
				},

				'&& .MuiAutocomplete-option.Mui-selected .MuiTypography-root': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
				},

				'&& .MuiAutocomplete-option.Mui-selected:hover': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-selected.Mui-focused': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-selected.Mui-focused .MuiTypography-root': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
				},
			},
		},
	};

	const getCommonInputLabelProps = (params: any) => ({
		...(params.InputLabelProps || {}),
		sx: {
			'&&': { color: `${colors.textLight.main}` },
			'&.Mui-focused': { color: `${colors.textLight.main}` },
			'&.MuiInputLabel-shrink': { color: `${colors.textLight.main}` },
		},
	});

	const commonOutlinedInputSx = {
		'&&': { color: `${colors.textLight.main}` },
		'& input': {
		color: `${colors.textLight.main}`,
		WebkitTextFillColor: `${colors.textLight.main}`,
		},
		'& .MuiSvgIcon-root': { color: `${colors.textLight.main}` },
		'& .MuiOutlinedInput-root': {
			'&.Mui-error': {
				'& .MuiOutlinedInput-notchedOutline': {
					borderColor: 'red'
				},
				'&:hover .MuiOutlinedInput-notchedOutline': {
					borderColor: 'red'
				},
				'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
					borderColor: 'red'
				}
			}
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.input.border} !important`
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.text.focus} !important`
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.text.focus} !important`
		},
		'& .MuiOutlinedInput-input': {
			fontSize: '12px',
			padding: '8px'
		}
	};

	const commonLightOutlinedInputSx = {
		'&&': { color: `${colors.textLight.main}` },
		'& input': {
		color: `${colors.textLight.main}`,
		WebkitTextFillColor: `${colors.textLight.main}`,
		},
		'& .MuiSvgIcon-root': { color: `${colors.textLight.main}` }, 
		'& .MuiOutlinedInput-input': {
			fontSize: '12px'
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.input.border} !important`
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.text.focus} !important`
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.text.focus} !important`
		}
	};

	useEffect(() => {
		const groupId = currentRow?.groupId;
		setGroupId(groupId);
		const resource = currentRow?.Resource;
		setResource(resource);
	}, [currentRow, allocationType.key]);

	// update payload function
	const updatePayload = () => {
		const updatedRows: { [key: string]: RowData } = {};
		selectedRows.forEach((r: any) => {
			if (r.isNew) return;

			const { startDate, endDate, PepwiseProject, PepwiseProgram, PepwiseSector, AllocPercent } = r;
			const formattedStartDate = startDate ? dayjs(startDate)?.format('YYYY-MM-DD') : '';
			const endDt = endDate === '' ? dayjs().format('YYYY-MM-DD') : dayjs(endDate)?.format('YYYY-MM-DD');
			const formattedEndDate = formattedStartDate && AllocPercent && AllocPercent > 0 ? endDt : '';

			const key = `${formattedStartDate}-${formattedEndDate}`;

			const allocation = {
				pepwiseSector: PepwiseSector,
				pepwiseProject: PepwiseProject,
				pepwiseProgram: PepwiseProgram,
				allocation: Number(AllocPercent),
				projectId: selectedProjectId[PepwiseProject] ? selectedProjectId[PepwiseProject] : Number(r.projectId),
				programId: selectedProgramId[PepwiseProgram] ? selectedProgramId[PepwiseProgram] : Number(r.programId)
			};

			if (!updatedRows[key]) {
				updatedRows[key] = {
					startDate: formattedStartDate,
					endDate: formattedEndDate,
					pepwiseEnvironment: environment,
					allocations: []
				};
			}

			updatedRows[key].allocations.push(allocation);
		});

		return updatedRows;
	};


	// Fetch program and project options
	const getInputProgramProjectOptions = async (input: string, type: string) => {
		return getProgramProjectHelper(
			input,
			type,
			true
		);
	};

	const getProgramProjectOptions = async (
		input: string | undefined,
		type: 'project' | 'program'
	): Promise<Option[]> => {
		const result = await getInputProgramProjectOptions(input ?? '', type);
		if (type === 'program') result ?? setProgramOptns(result);
		else if(type === 'project') result ?? setProjectOptions(result);
		return result;
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				setIsLoading(true);
				if (groupId) {
					const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
					const response = await getResourceAllocationFinops({ groupId: groupId, userTimezone: userTimezone });
					if (response.success && response.data) {
						const transformedData = response.data.map((item: any) => ({
							id: item.id,
							allocId: item.allocationId,
							isNew: false,
							isEditing: false,
							PepwiseEnvironment: item.pepwiseEnvironment,
							PepwiseProject: item.pepwiseProject,
							PepwiseProgram: item.pepwiseProgram,
							PepwiseSector: item.pepwiseSector,
							AllocPercent: item.allocation,
							startDate: item.startDate,
							endDate: item.endDate,
							programId: item.programId,
							projectId: item.projectId,
							updatedBy: item.updatedBy,
							updatedDate: item.updatedDate
						}));
						setEnvironment(response.data[0].pepwiseEnvironment);
						setOriginalEnvironment(response.data[0].pepwiseEnvironment);
						setSelectedRows(transformedData);
						setOriginalRows(transformedData);
					}
				}
			} catch (err) {
				console.error('Error fetching allocation:', err);
				return [];
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, [groupId]);

	// Helper function to check if data has changed
	const hasChanges = () => {
		if (!originalRows || originalRows.length !== selectedRows.length || environment !== originalEnvironment) {
			return true;
		}
		return selectedRows.some((row, index) => {
			const originalRow = originalRows[index];
			return (
				row.PepwiseProject !== originalRow.PepwiseProject ||
				row.PepwiseProgram !== originalRow.PepwiseProgram ||
				row.PepwiseSector !== originalRow.PepwiseSector ||
				row.AllocPercent !== originalRow.AllocPercent ||
				row.startDate !== originalRow.startDate ||
				row.endDate !== originalRow.endDate
			);
		});
	};

	useEffect(() => {
		const fetchOptions = async () => {
			if(currentRow.AllocationCount === 0) {
				setEnvironment(currentRow.Environment);
				setOriginalEnvironment(currentRow.Environment);
			}

			const [programOptions, projectOptions] =
				await Promise.all([
					getInputProgramProjectOptions('', 'program'),
					getInputProgramProjectOptions('', 'project'),
				]);
			setProgramOptns(programOptions);
			setProjectOptions(projectOptions);

			setNewAllocation((prev) => {
				const programMatch = programOptions?.some((opt: any) => opt.label === currentRow.Program);
				const projectMatch = projectOptions?.some((opt: any) => opt.label === currentRow.ProjectName);
				return {
				  ...prev,
				  PepwiseProgram: programMatch ? currentRow.Program : '',
				  PepwiseProject: projectMatch ? currentRow.ProjectName : '',
				  PepwiseSector: currentRow.sector ? currentRow.sector : '',
				};
		  	});
		};
		fetchOptions();
		getInputSecEnvOptions();
		handleAddNewRow();
	}, []);

	// fetch sector and environment options
	const getInputSecEnvOptions = async () => {
		try {
			const fieldsRes = await getBulkEditFinopsinputSecEnvOption();

			const sectorsOptions = fieldsRes.data.sectors.filter(Boolean).map((item: string) => ({
				value: item,
				label: item
			}));

			const environmentsOptions = [{ value: '', label: '' }, ...fieldsRes.data.environments
				.filter((item: string) => item).map((item: string) => ({
					value: item,
					label: item,
				})),
			];
			setSectorOptions(sectorsOptions);
			setEnvironmentOptions(environmentsOptions);
		} catch (error) {
			console.error('Error fetching options:', error);
			return [];
		}
	};
	
	const handleChange = (field: string, value: any) => {
		setNewAllocation((prev) => {
			  // Otherwise, just update the specified field
			  return {
				...prev,
				[field]: value,
			  };
		});
		
		// validation based on the field
		switch (field) {
			case 'AllocPercent':
				setValidationErrors((prev) => ({
					...prev,
					allocationPercentage: value && (value < 0 || value > 100 || isNaN(value))
				}));
				break;
			case 'startDate':
				setValidationErrors((prev) => ({
					...prev,
					allocationStartTime: newAllocation.AllocPercent && Number(newAllocation.AllocPercent) > 0 ? !value : false,
					allocationEndTime: prev.allocationEndTime && value && dayjs(value).isAfter(newAllocation.endDate)
				}));
				break;
			case 'endDate':
				setValidationErrors((prev) => ({
					...prev,
					allocationEndTime:
						value !== '' &&
						(dayjs(value).isAfter(dayjs(), 'day') ||
							(newAllocation.startDate && !dayjs(value).isAfter(dayjs(newAllocation.startDate))))
				}));
				break;
			default:
				break;
		}
	};

	const handleEditRow = (row: TableRow) => {
		setCurrentEditRow(row);
		setSelectedRows((prevRows) =>
			prevRows.map((r) => (r.id === row.id ? { ...r, isEditing: true } : { ...r, isEditing: false }))
		);
	};

	const handleSaveEdit = (rowId: number, updatedFields: { [key: string]: any }) => {
		setSelectedRows((prevRows) => 
		  prevRows.map((row) => {
			if (row.id === rowId) {
				return { ...row, ...updatedFields, isEditing: false };
			}
			return row;
		  })
		);
	};

	const handleAllocationChange = (id: number, field: string, value: any) => {
		setCurrentEditRow((prev) => {
			  // Otherwise, just update the specified field
			  return {
				...prev,
				[field]: value,
			  };
		});
		if (field === 'allocationEndTime' && value === '') {
			value = dayjs().format('YYYY-MM-DD');
		}
		setSelectedRows(selectedRows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
	};

	const handleAddAllocation = () => {
		const generateRandomId = () => Math.floor(Math.random() * 1000000);
		// Remove the existing row with isNew: true, if any
		const rowsWithoutNew = selectedRows.filter((row) => !row.isNew);
		const newId = generateRandomId();
		const hasDuplicate = selectedRows.some(
			(selectedRow) =>
				selectedRow.id !== newAllocation.id &&
				selectedRow.endDate === newAllocation.endDate &&
				selectedRow.PepwiseProject === newAllocation.PepwiseProject &&
				selectedRow.PepwiseProgram === newAllocation.PepwiseProgram &&
				selectedRow.PepwiseSector === newAllocation.PepwiseSector
		);
		setIsDuplicate(hasDuplicate);
		const errors = {
			environment: false,
			project: !!hasDuplicate, 
			programs: !!hasDuplicate ,
			sectors: false,
			allocationPercentage: (newAllocation.AllocPercent && Number(newAllocation.AllocPercent) < 0 
				|| Number(newAllocation.AllocPercent) > 100 || isNaN(Number(newAllocation.AllocPercent))),
			allocationStartTime: newAllocation.AllocPercent && Number(newAllocation.AllocPercent) > 0 ? !newAllocation.startDate : false, // Start time is required
			allocationEndTime:
			(newAllocation.endDate.toString() !== '' &&
				(
					dayjs(newAllocation.endDate).isAfter(dayjs()) || 
					(newAllocation.startDate && 
						!dayjs(newAllocation.endDate).isAfter(newAllocation.startDate))
				)
			)
		};
		setValidationErrors(errors);

		const singleError = (!newAllocation.PepwiseProgram && !newAllocation.PepwiseProject && !newAllocation.PepwiseSector 
			&& !environment) ? true : false;
		setSingleValidationError(singleError);

		// Add the new allocation to the updated list
		if (!Object.values(errors).some((error) => error) && !singleError) {
			setSelectedRows([...rowsWithoutNew, { ...newAllocation, id: newId, isNew: false, isEditing: false }]);
			setNewAllocation({
				id: 0,
				allocId: 0,
				isNew: false,
				isEditing: false,
				PepwiseEnvironment: '',
				PepwiseProject: '',
				PepwiseProgram: '',
				PepwiseSector: '',
				AllocPercent: '',
				startDate: dayjs(),
				endDate: ''
			});
			setValidationErrors(validationInitialState);
			setNewAllocationRecords({
				...newAllocationRecords,
				[newId]: { ...newAllocation, id: newId, isNew: false, isEditing: false }
			});
		}
	};

	const handleDeleteNewRow = (row:any) => {
		setSelectedRows(selectedRows.filter((row) => !row.isNew ))
	};

	const handleAddNewRow = () => {
		setValidationErrors(validationInitialState);
		
		// check there's only one new row at a time
		if (isNewAllocRowExists) return;
		
		const totalAllocation = selectedRows.reduce((sum, row) => {
			if (
				row.startDate === selectedRows[selectedRows.length - 1]?.startDate &&
				row.endDate === selectedRows[selectedRows.length - 1]?.endDate
			) {
				return sum + Number(row.AllocPercent);
			}
			return sum;
		}, 0);

		if (totalAllocation !== 0 && totalAllocation < 100) {
			const lastRow = selectedRows[selectedRows.length - 1];
			setNewAllocation(
				{
					id: 0,
					allocId: 0,
					isNew: true,
					isEditing: false,
					PepwiseEnvironment: '',
					PepwiseProject: '',
					PepwiseProgram: '',
					PepwiseSector: '',
					AllocPercent: '',
					startDate: lastRow?.startDate,
					endDate: lastRow?.endDate
				}
			);
		} else if(totalAllocation === 100) {
			const lastRow = selectedRows[selectedRows.length - 1];
			const baseDate = lastRow?.endDate ? dayjs(lastRow.endDate).add(1, 'day') : dayjs().add(1, 'day');
			setNewAllocation(
				{
					id: 0,
					allocId: 0,
					isNew: true,
					isEditing: false,
					PepwiseEnvironment: '',
					PepwiseProject: '',
					PepwiseProgram: '',
					PepwiseSector: '',
					AllocPercent: '100',
					startDate: baseDate,
					endDate: ''
				}
			);
		}else{
			const lastRow = selectedRows[selectedRows.length - 1];
			setNewAllocation(
				{
					id: 0,
					allocId: 0,
					isNew: true,
					isEditing: false,
					PepwiseEnvironment: '',
					PepwiseProject: '',
					PepwiseProgram: '',
					PepwiseSector: '',
					AllocPercent: '100',
					// startDate: (currentRow.AllocationCount === 0 || (selectedRows.length === 0 && currentRow.AllocationCount > 0)) ? dayjs(currentRow.RescCreatedDate) : dayjs(),
					startDate: selectedRows.length === 0 ? dayjs(currentRow.RescCreatedDate) : lastRow?.startDate,
					endDate: ''
				}
			);
		}

		setSelectedRows([
			...selectedRows,
			{
				id: 0,
				allocId: 0,
				isNew: true,
				isEditing: false,
				PepwiseEnvironment: '',
				PepwiseProject: '',
				PepwiseProgram: '',
				PepwiseSector: '',
				AllocPercent: '',
				// startDate: (currentRow.AllocationCount === 0 || (selectedRows.length === 0 && currentRow.AllocationCount > 0)) ? dayjs(currentRow.RescCreatedDate) : dayjs(),
				startDate: selectedRows.length === 0 ? dayjs(currentRow.RescCreatedDate) : dayjs(),
				endDate: ''
			}
		]);
	};

	const handleAllocationClear = async () => {
		onTitleChange('Create/Modify Allocation');
		onWidthChange('lg');

		// Reset `currentRow` fields
		Object.assign(currentRow, {
			AllocPercent: '',
			AllocationCount: 0,
			PepwiseEnvironment: '',
			PepwiseProgram: '',
			PepwiseProject: '',
			PepwiseSector: '',
			ProgramCount: 0,
			ProjectCount: 0,
			SectorCount: 0
		});
		setShowClearAllocModal(false);

		const programMatch = programOptns?.some((opt: any) => opt.label === currentRow.Program);
		const projectMatch = projectOptions?.some((opt: any) => opt.label === currentRow.ProjectName);

		const newAllocation = {
			id: 0,
			allocId: 0,
			isNew: true,
			isEditing: false,
			PepwiseEnvironment: '',
			PepwiseProject: projectMatch ? currentRow.ProjectName : '',
			PepwiseProgram: programMatch ? currentRow.Program : '',
			PepwiseSector: currentRow.sector ? currentRow.sector : '',
			AllocPercent: '100',
			startDate: currentRow.AllocationCount === 0 ? dayjs(currentRow.RescCreatedDate) : dayjs(),
			endDate: '' as const
		};
		setNewAllocation(newAllocation);
		setSelectedRows([newAllocation]);
		setEnvironment('');
		setOriginalEnvironment('');
	};
	
	const createAllocation = async (groupId: string, resource: string, payload: RowData[] | []) => {
		try {
			setIsLoading(true);
			const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const res = (await postResourceAllocationFinops({
				groupId: groupId,
				resource: resource,
				data: payload,
				userTimezone: userTimezone
			})) as { response: any; data?: any; success?: boolean };

			// // Check if `BulkEditRes` exists (success case)
			if (res.success) {
				const message = res.data.result;
				if(message === 'Allocations deleted successfully') {
					await handleAllocationClear();
					return false;
				}
				else {
					if (res.success) {
						onTitleChange('Allocation Modified');
						onWidthChange('md');
						setResponse(res.data.result);
						setErrorRes(res.data?.errorData)
						setIsSuccessModalOpen(true);
						return true;
					} else {
						// Handle failure when success flag is false
						onTitleChange('Create/Modify Allocation');
						onWidthChange('lg');
						setResponse(res.data);
						setIsSuccessModalOpen(false);
						return false;
					}
				}
			} else if (res.response.data) {
				const { success } = res.response.data;

				if (success) {
					onTitleChange('Allocation Modified');
					onWidthChange('md');
					setResponse(res.response.data);
					setIsSuccessModalOpen(true);
					return true;
				} else {
					// Handle failure when success flag is false
					onTitleChange('Create/Modify Allocation');
					onWidthChange('lg');
					setResponse(res.response.data);
					setIsSuccessModalOpen(false);
					return false;
				}
			}
		} catch (error) {
			console.log('Error in create allocation:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleConfirm = async () => {
		if (Object.values(validationErrors).includes(true) || remainingPercentage !== 0) {
			return;
		}
		const updatedPayload = updatePayload();
		await createAllocation(groupId, resource, Object.values(updatedPayload));
	};

	const handleCancel = () => {
		const newRecordIds = Object.keys(newAllocationRecords).map(Number);

		setSelectedRows((prevRows) => prevRows.filter((row) => !newRecordIds.includes(row.id)));
		setNewAllocationRecords({});
		if(isClearAllocation) onConfirm();
		onCancel();
	};

	const handleClearAllocation = async (formData: any) => {
		await createAllocation(formData.groupId, formData.resource, []);
		setIsClearAllocations(true);
		if(currentRow.AllocationCount === 0) {
			setEnvironment(currentRow.Environment);
			setOriginalEnvironment(currentRow.Environment);
		}
	};

	const handleClearCancel = () => {
		onTitleChange('Create/Modify Allocation');
		onWidthChange('lg');
		setShowClearAllocModal(false);
	};

	const handleClearAllocationsClick = () => {
		onTitleChange('Clear Allocations');
		onWidthChange('sm');
		setFormData((prevState) => ({
			...prevState,
			groupId: groupId,
			resource: resource
		}));
		setShowClearAllocModal(true);
	};

	const handleDeleteRow = (id: number) => {
		setSelectedRows(selectedRows.filter((row) => row.id !== id));
		setNewAllocationRecords((prevRecords) => {
			const updatedRecords = { ...prevRecords };
			delete updatedRecords[id];
			return updatedRecords;
		});
	};

	function renderReadOnlyCell(row: any, column: any) {
		const value = row[column.key];

		let content: React.ReactNode;
		if (column.key === 'PepwiseSector' && value) {
			content = (
				<Stack direction="row" spacing={1}>
					<RowChip value={value} rowId={row.id} bgColor={colors.table.chipBackground} color={colors.table.chipText}/>
				</Stack>
			);
		} else if (dayjs.isDayjs(value)) {
			content = value.format('YYYY-MM-DD');
		} else if (Array.isArray(value)) {
			content = value.map((item: string, index: number) => (
				<Typography key={`${index}-${item}`}>{item}</Typography>
			));
		} else if (column.key === 'AllocPercent') {
			content = value ? `${value}%` : '';
		} else if (column.key === 'endDate' && value === '') {
			content = row['AllocPercent'] && row.AllocPercent > 0 ? dayjs().format('YYYY-MM-DD') : '';
		} else {
			content = value;
		}

		return (
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<RowTypography isSuccessor={!value}>{content}</RowTypography>
			</Stack>
		);
	}

	function renderActionCell(row: any) {
		return row.isEditing ? (
			<Stack direction="row" alignItems="center" justifyContent="space-evenly" height='100%'>
				<CloseIcon className='rm-icons' title="Cancel" 
					onClick={() => {
						setEditValidationErrors(validationInitialState);
						setSingleValidationError(false);
						setIsDuplicate(false);
						handleSaveEdit(row.id, row.originalData);
						delete row.originalData;
					}} 
				/>
				<SaveIcon title="Save" className='rm-icons'
					onClick={() => {
						const hasDuplicate = selectedRows.some(
							(selectedRow) =>
								selectedRow.id !== row.id &&
								selectedRow.endDate === row.endDate &&
								selectedRow.PepwiseProject === row.PepwiseProject &&
								selectedRow.PepwiseProgram === row.PepwiseProgram &&
								selectedRow.PepwiseSector === row.PepwiseSector
						);
						setIsDuplicate(hasDuplicate);
						const singleError = (!row.PepwiseProgram && !row.PepwiseProject 
							&& !row.PepwiseSector && !environment) ? true : false;
						setSingleValidationError(singleError);
						const hasError = (row.AllocPercent && row.AllocPercent > 0) && (!row.PepwiseProgram && !row.PepwiseProject 
							&& !row.PepwiseSector && !environment) && (!row.startDate || !row.endDate) ? true : false;
							setEditValidationErrors((prev) => ({
							...prev,
							programs: !!hasDuplicate,
							project: !!hasDuplicate,
							allocationStartTime: (row.AllocPercent && row.AllocPercent > 0) && !row.startDate ? true :false,
						}));
						if (
							!editValidationErrors.project &&
							!editValidationErrors.programs&&
							!editValidationErrors.sectors &&
							!editValidationErrors.allocationPercentage &&
							!editValidationErrors.allocationStartTime &&
							!editValidationErrors.allocationEndTime &&
							!singleError && !hasError && !hasDuplicate
						) {
							handleSaveEdit(row.id, {
								project:row.project,
								programs:row.programs,
								sectors:row.sectors,
								allocationPercentage: row.allocationPercentage,
								allocationStartTime: row.allocationStartTime,
								allocationEndTime: row.allocationEndTime
							});
							delete row.originalData;
						}
					}}
				/>
			</Stack>
		) : (
			<Stack direction="row" alignItems="center" justifyContent="space-evenly">
				<EditIcon style={{ cursor: 'pointer' }} title="Click to Edit" onClick={() => handleEditRow(row)} />
				<DeleteIcon
					title="Delete Row"
					style={{ width: '30px', height: '30px', cursor: 'pointer' }}
					onClick={() => handleDeleteRow(row.id)}
				/>
			</Stack>
		);
	}

	function renderEditCell(row: any, column: any, col: any) {
		if (col.name === 'PepwiseProgram') {
			return (
				<FormControl fullWidth error={!!editValidationErrors.programs}>
					<AsyncAutoComplete
						inputValue={currentEditRow.PepwiseProgram}
						value={currentEditRow.PepwiseProgram !== null ? currentEditRow.PepwiseProgram : row[column.key]}
						onChange={(event, value) => {
							const selectedProgram = programOptns.find((option) => option.label === value);
							const programId = selectedProgram && Number(selectedProgram.value);

							programId && setSelectedProgramId((prev) => ({ ...prev, [value]: programId }));
							handleAllocationChange(row.id, column.key, value);
						}}
						mappingType=""
						columnName={'PepwiseProgram'}
						options={programOptns}
						fieldValueOptionsCallback={async (input) => await getProgramProjectOptions(input, 'program')}
						style={{ width: '9.5rem' }}
						sx={commonInputSx}
						customTextFieldProps={commonTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon sx={{ color: colors.DropdownV2.dropdownIconDefault }} />}
						componentsProps={commonAutocompleteComponentsProps}
					/>
				</FormControl>
			);
		}

		if (col.name === 'PepwiseProject') {
			return (
				<FormControl fullWidth error={!!editValidationErrors.project}>
					<AsyncAutoComplete
						textFieldLabel="Project"
						value={currentEditRow.PepwiseProject}
						inputValue={currentEditRow.PepwiseProject}
						options={projectOptions}
						onChange={(event, value) => {
							const selectedProject = projectOptions.find((option) => option.label === value);
							const projectId = selectedProject && Number(selectedProject.value);
							projectId && setSelectedProjectId((prev) => ({ ...prev, [value]: projectId }));
							handleAllocationChange(row.id, column.key, value);
						}}
						fieldValueOptionsCallback={async (input) => await getProgramProjectOptions(input,'project')}
						mappingType=""
						columnName={'PepwiseProject'}
						style={{ width: '9.5rem' }}
						sx={{
							'& .MuiInputLabel-root': {
								display: 'none' // Hides the label completely
							},
							'& .MuiOutlinedInput-notchedOutline > legend': {
								width: '0px' // Keeps the legend box but removes its text
							},
							...commonInputSx
						}}
						customTextFieldProps={commonTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon sx={{ color: colors.DropdownV2.dropdownIconDefault }} />}
						componentsProps={commonAutocompleteComponentsProps}
					/>
				</FormControl>
			);
		}

		if (col.name === 'PepwiseSector') {
			return (
				<FormControl fullWidth error={!!editValidationErrors.sectors}>
					<AsyncAutoComplete
						value={row[column.key]}
						inputValue={row[column.key]}
						onChange={(event, value) => {
							handleAllocationChange(row.id, column.key, value);
						}}
						mappingType=""
						columnName={'PepwiseSector'}
						options={sectorOptions}
						style={{ width: '9.5rem' }}
						sx={commonInputSx}
						customTextFieldProps={commonTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon sx={{ color: colors.DropdownV2.dropdownIconDefault }} />}
						componentsProps={commonAutocompleteComponentsProps}
					/>
				</FormControl>
			);
		}
		if (col.name === 'AllocPercent') {
			return (
				<FormControl fullWidth error={!!editValidationErrors.allocationPercentage}>
					<TextField
						sx={{
							'& .MuiOutlinedInput-root': {
								'&.Mui-error': {
									'& .MuiOutlinedInput-notchedOutline': {
										borderColor: 'red'
									},
									'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: 'red'
									},
									'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
										borderColor: 'red'
									}
								}
							},
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: `${colors.input.border} !important`
							},
							'&:hover .MuiOutlinedInput-notchedOutline': {
								borderColor: `${colors.text.focus} !important`
							},
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
								borderColor: `${colors.text.focus} !important`
							},
							'& .MuiOutlinedInput-input': {
								fontSize: '12px',
								padding: '8px',
								color:`${colors.textLight.main} !important`
							}
						}}
						error={!!editValidationErrors.allocationPercentage}
						value={row[column.key]}
						onChange={(e) => {
							const value = e.target.value;
							handleAllocationChange(row.id, column.key, value);
							// Validate allocationPercentage
							if (value && (Number(value) < 0 || Number(value) > 100 || isNaN(Number(value)))) {
								setEditValidationErrors((prev) => ({
									...prev,
									allocationPercentage: true
								}));
							} else {
								setEditValidationErrors((prev) => ({
									...prev,
									allocationPercentage: false,
									programs: false,
									project: false,
									sectors: false,
									allocationStartTime: false,
									allocationEndTime: false,
								}));
							}
						}}
					/>
				</FormControl>
			);
		}
		if (col.name === 'startDate') {
			const minStartDate = selectedRows
			.map(({ startDate }) => dayjs(startDate))
			.reduce((min, date) => (date.isBefore(min) ? date : min), dayjs(selectedRows[0].startDate));
			let cumulative = 0;
			let lastIndex = -1;
			selectedRows.forEach((rowItem, index) => {
				const percent = Number(rowItem.AllocPercent) || 0;
				if (percent > 0) {
					cumulative += percent;
					if (cumulative === 100) {
					lastIndex = index;
					cumulative = 0; // Reset for next group
					}
				} else {
					// No AllocPercent given → separate group
					cumulative = 0;
					lastIndex = index; // Treat it as end of a group
				}
				});
			const currentRowIndex = selectedRows.findIndex(r => r.id === row.id);
			return (
				<FormControl fullWidth error={!!editValidationErrors.allocationStartTime}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							value={row[column.key]}
							minDate={minStartDate}
							disabled={row[column.key] && selectedRows[0].id === row.id ? true : false}
							maxDate={currentRowIndex < lastIndex && row[column.key] ? dayjs(row[column.key]) : null}
							onChange={(newValue) => {
								const formattedValue = newValue && dayjs(newValue).format('YYYY-MM-DD');

								handleAllocationChange(row.id, column.key, formattedValue);
								// Validate allocationStartTime
								if (currentEditRow.AllocPercent && !formattedValue) {
									setEditValidationErrors((prev) => ({
										...prev,
										allocationStartTime: true
									}));
								} else {
									setEditValidationErrors((prev) => ({
										...prev,
										allocationStartTime: false
									}));
								}
							}}
							PopperProps={commonPopperProps}
							renderInput={(params) => (
								<TextField
									{...params}
									InputLabelProps={getCommonInputLabelProps(params)}
									error={!!editValidationErrors.allocationStartTime}
									sx={commonOutlinedInputSx}
								/>
							)}
						/>
					</LocalizationProvider>
				</FormControl>
			);
		}
		if (col.name === 'endDate') {
			return (
				<FormControl fullWidth error={!!editValidationErrors.allocationEndTime}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							value={row[column.key]}
							maxDate={dayjs()}
							onChange={(newValue) => {
								const formattedValue = newValue ? dayjs(newValue).format('YYYY-MM-DD') : '';
								handleAllocationChange(row.id, column.key, formattedValue);

								if (
									(formattedValue !== '' &&
									row.allocationStartTime &&
									dayjs(formattedValue).isBefore(dayjs(row.allocationStartTime), 'day')) ||
									(formattedValue !== '' && dayjs(formattedValue).isAfter(dayjs(), 'day'))
								) {
									// End date is before start date
									setEditValidationErrors((prev) => ({
										...prev,
										allocationEndTime: true
									}));
								} else {
									// Valid end date
									setEditValidationErrors((prev) => ({
										...prev,
										allocationEndTime: false
									}));
								}
							}}
							PopperProps={commonPopperProps}
							renderInput={(params) => (
								<TextField
									{...params}
									InputLabelProps={getCommonInputLabelProps(params)}
									error={!!editValidationErrors.allocationEndTime}
									sx={commonOutlinedInputSx}
								/>
							)}
						/>
					</LocalizationProvider>
				</FormControl>
			);
		}
		return null;
	}

	function renderNewRowCell(row: any, col: any) {
		if (!row.isNew) return null;
		if (col.name === 'PepwiseProgram') {
			return (
				<FormControl fullWidth error={validationErrors.programs}>
					<AsyncAutoComplete
						textFieldLabel="Programs"
						inputValue={newAllocation.PepwiseProgram}
						value={newAllocation.PepwiseProgram}
						onChange={(event, value) => {
							const selectedProgram = programOptns.find((option) => option.label === value);
							const programId = selectedProgram && Number(selectedProgram.value);
							
							programId && setSelectedProgramId((prev) => ({...prev, [value]: programId}));
							handleChange('PepwiseProgram', value);
						}}
						mappingType=""
						columnName={'PepwiseProgram'}
						options={programOptns}
						fieldValueOptionsCallback={async (input) => await getProgramProjectOptions(input, 'program')}
						style={{ width: '9.5rem' }}
						sx={commonInputSx2}
						customTextFieldProps={commonTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon sx={{ color: colors.DropdownV2.dropdownIconDefault }} />}
						componentsProps={commonAutocompleteComponentsProps}
					/>
					{validationErrors.programs && !isDuplicate && <FormHelperText>{'Programs is required.'}</FormHelperText>}
				</FormControl>
			);
		}

		if (col.name === 'PepwiseProject') {
			return (
				<FormControl fullWidth error={validationErrors.project}>
					<AsyncAutoComplete
						textFieldLabel="Project"
						inputValue={newAllocation.PepwiseProject}
						value={newAllocation.PepwiseProject}
						onChange={(event, value) => {
							const selectedProject = projectOptions.find((option) => option.label === value);
							const projectId = selectedProject && Number(selectedProject.value);

							projectId && setSelectedProjectId((prev) => ({...prev, [value]: projectId}));
							handleChange('PepwiseProject', value);
						}}
						mappingType=""
						columnName={'PepwiseProject'}
						options={projectOptions}
						fieldValueOptionsCallback={async (input) => await getProgramProjectOptions(input, 'project')}
						style={{ width: '9.5rem' }}
						sx={commonInputSx2}
						customTextFieldProps={commonTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon sx={{ color: colors.DropdownV2.dropdownIconDefault }} />}
						componentsProps={commonAutocompleteComponentsProps}
					/>
					{validationErrors.project && !isDuplicate && <FormHelperText>{'Project is required.'}</FormHelperText>}
				</FormControl>
			);
		}

		if (col.name === 'PepwiseSector') {
			return (
				<FormControl fullWidth error={validationErrors.sectors}>
					<AsyncAutoComplete
						textFieldLabel="Sectors"
						inputValue={newAllocation.PepwiseSector}
						value={newAllocation.PepwiseSector}
						onChange={(event, value) => handleChange('PepwiseSector', value)}
						mappingType="finopsResourceMapping"
						columnName={'PepwiseSector'}
						options={sectorOptions}
						style={{ width: '9.5rem' }}
						sx={commonInputSx2}
						customTextFieldProps={commonTextFieldProps}
						popupIcon={<KeyboardArrowDownIcon sx={{ color: colors.DropdownV2.dropdownIconDefault }} />}
						componentsProps={commonAutocompleteComponentsProps}
					/>
					{validationErrors.sectors && <FormHelperText>Sectors is required.</FormHelperText>}
				</FormControl>
			);
		}

		if (col.isEditable && col.name === 'AllocPercent') {
			return (
				<FormControl fullWidth error={validationErrors.allocationPercentage}>
					<TextField
						type="text"
						variant="outlined"
						label="Allocation %"
						value={newAllocation.AllocPercent === '0' ? '' : newAllocation.AllocPercent}
						sx={{
							'& .MuiInputLabel-root': {
								color: `${colors.input.text} !important`
							},
							'& .MuiInputLabel-root.Mui-focused': {
								color: `${colors.input.text} !important`
							},
							'& .MuiInputLabel-root.MuiInputLabel-shrink': {
								color: `${colors.input.text} !important`
							},
							'& .MuiOutlinedInput-root': {
								'& fieldset': {
									borderColor: `${colors.input.border} !important`
								},
								'&:hover fieldset': {
									borderColor: 'text.focus'
								},
								'&.Mui-focused fieldset': {
									borderColor: 'text.focus'
								}
							},
							'& .MuiOutlinedInput-input': {
								fontSize: '12px',
								width: '9.5rem',
								color:`${colors.textLight.main} !important`
							}
						}}
						onChange={(e) => handleChange(col.name, e.target.value)}
					/>
					{validationErrors.allocationPercentage && (
						<FormHelperText sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5 }}>
							Allocation percentage must be between 0 and 100.
						</FormHelperText>
					)}
				</FormControl>
			);
		}
		if (col.name === 'startDate') {
			const nextSameAlloc = selectedRows.length > 1 && selectedRows[selectedRows.length - 2]?.AllocPercent
			&& Number(selectedRows[selectedRows.length - 2]?.AllocPercent) !== 0 
			&& Number(selectedRows[selectedRows.length - 2]?.AllocPercent) < 100 
			&& selectedRows[selectedRows.length - 2]?.allocId === 0;
			return (
				<FormControl fullWidth error={validationErrors.allocationStartTime}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							value={newAllocation.startDate}
							minDate={dayjs(currentRow.RescCreatedDate)}
							onChange={(newValue) => {handleChange(col.name, dayjs(newValue).format('YYYY-MM-DD'))
							}}
							disabled={selectedRows.length === 1 
								|| nextSameAlloc || (selectedRows.length === 1 && currentRow.AllocationCount > 0)}
							PopperProps={commonPopperProps}
							renderInput={(params) => (
								<TextField
									{...params}
									InputLabelProps={getCommonInputLabelProps(params)}
									sx={commonLightOutlinedInputSx}
								/>
							)}
						/>
					</LocalizationProvider>
					{validationErrors.allocationStartTime && (
						<FormHelperText sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5 }}>
							Allocation Start date is required.
						</FormHelperText>
					)}
				</FormControl>
			);
		}

		if (col.name === 'endDate') {
			return (
				<FormControl fullWidth error={validationErrors.allocationEndTime}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							value={newAllocation.endDate}
							maxDate={dayjs()}
							onChange={(newValue) =>
								!newValue
									? handleChange(col.name, '')
									: handleChange(col.name, dayjs(newValue).format('YYYY-MM-DD'))
							}
							PopperProps={commonPopperProps}
							renderInput={(params) => {
								
								return <TextField
									{...params}
									InputLabelProps={getCommonInputLabelProps(params)}
									sx={commonLightOutlinedInputSx}
								/>
							}}
						/>
					</LocalizationProvider>
					{validationErrors.allocationEndTime && (
						<FormHelperText
							sx={{
								whiteSpace: 'normal',
								wordBreak: 'break-word',
								lineHeight: 1.5
							}}
						>
							End date is required and must be after start date, cannot be a future date.
						</FormHelperText>
					)}
				</FormControl>
			);
		}
		if (col.name === 'actions') {
			return (
				<Stack
					direction="row"
					alignItems="center"
					spacing="0px !important"
					justifyContent="space-between"
					sx={{
						height: '50%',
						width: '100%'
					}}
				>
					<Alert
						severity="success"
						sx={{
							width: '8px',
							cursor: 'pointer',
							'& .MuiAlert-icon': {
								color: 'info.main'
							},
							backgroundColor: 'transparent !important'
						}}
						onClick={handleAddAllocation}
					></Alert>
					<DeleteIcon className='rm-del-icons'
						title="Delete Row"
						onClick={() => handleDeleteNewRow(row)}
					/>
				</Stack>
			);
		}
		return null;
	}

	const AllocationCell = ({ row, column, col }: any) => {
		// Preserve original row snapshot
		if (row.isEditing && !row.originalData) row.originalData = { ...row };

		if (row.isNew) {
			return renderNewRowCell(row, col);
		}
		if (col.isEditable && row.isEditing) {
			return renderEditCell(row, column, col);
		}
		if (col.name === 'actions') {
			return renderActionCell(row);
		}
		return renderReadOnlyCell(row, column);
	};

	const allocationFormatter = (col: any) => {
		const renderCell = ({ row, column }: any) => (
			<AllocationCell
				row={row}
				column={column}
				col={col}
			/>
		);
		return renderCell;
	};

	function getColumns(): Array<any> {
		return columnNames.map((col: any, idx: number) => ({
			key: col.name,
			name: col.label,
			editable: () => col.isEditable,
			formatter: allocationFormatter(col),
			width: col.name === 'actions' ? 50 : 212
		}));
	}

	// scroll to table input field once add row clicked
	const gridRef = useRef<any>();
	useEffect(() => {
		if (isNewAllocRowExists) {
			if (gridRef.current) {
				gridRef.current.scrollToRow(selectedRows.length - 1);
			}
		}
	}, [selectedRows]);

	if (isLoading) {
		return <LoaderComponent p={2} bgColor={`${colors.modal.bg}`} />;
	}

	if (showClearAllocModal) {
		return (
			<ClearAllocationsModal
				onCancel={handleClearCancel}
				formData={formData}
				handleSave={handleClearAllocation}
			/>
		);
	}
	
	return !isSuccesModalOpen ? (
		<Stack sx={{ backgroundColor: `${colors.modal.bg} !important`, color: `${colors.modal.text} !important`}}>
		{response?.message && (
			<Stack
				direction="row"
				py="0.5rem"
				px="1.5rem"
				sx={{ backgroundColor: 'boxColor.orange.bgColor', display: 'flex', alignItems: 'center' }}
			>
				<WarningImage className="warning-image" />
				<Typography variant="tableColumnFont" sx={{ color: `${colors.modal.text} !important`, ml: '0.5rem' }}>
					{response?.message && (
						<>
							{response?.message}{' '}
						</>
					)} 
					{response?.data?.startDate && (
						<>
							( Start Date: {new Date(response.data.startDate).toLocaleDateString()}, End Date: {new Date(response.data.endDate).toLocaleDateString()} )
						</>
					)}
				</Typography>
				{response?.data?.overlappingDates &&
					response.data.overlappingDates.length > 0 &&
					response.data.overlappingDates.map((overlap: any, index: any) => (
						<Typography key={`overlap-${overlap?.startDate ?? ''}-${index}`} variant="tableColumnFont" sx={{ color: `${colors.modal.text} !important`, ml: '0.5rem' }}>
							<br />
							Start Date: {new Date(overlap?.startDate).toLocaleDateString()}
							<br />
							End Date:{' '}
							{new Date(overlap?.endDate).toLocaleDateString()}
							<br />
							Overlapping With:
							<br />
							Start Date: {new Date(overlap?.overlappingWith?.startDate).toLocaleDateString()}
							<br />
							End Date:{' '}
							{new Date(overlap?.overlappingWith?.endDate).toLocaleDateString()}
						</Typography>
					))}
			{response?.data?.futureDates &&
					response.data.futureDates.length > 0 &&
					response.data.futureDates.map((dates: any, index: any) => (
						
						
						<Typography key={`missed-${dates?.startDate ?? ''}-${index}`} variant="tableColumnFont" sx={{ color: `${colors.modal.text} !important`, ml: '0.5rem' }}>
							<br />
							Start Date: {new Date(dates?.startDate)?.toLocaleDateString()}
							<br />
							End Date:{' '}
							{dates?.endDate ? new Date(dates?.endDate).toLocaleDateString() : new Date().toLocaleDateString()}
							<br />
							future Date Type:{' '}{dates?.futureDateType}
							<br />
						</Typography>
					))}
			</Stack>
		)}
			<Stack
				sx={{
					flexDirection: 'column',
					pl: '20px',
					pr: '20px',
					pb: '20px',
					gap: 2,
					textAlign: 'left',
					'& .rdg-cell': {
						'&:hover': {
							backgroundColor: 'inherit !important'
						}
					}
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						color: 'error.main',
						textAlign: 'center',
						width: '100%',
						fontSize: '12px'
					}}
				>
					{editValidationErrors.programs && !isDuplicate && (
						<Typography>{editErrorMessages.programs}</Typography>
					)}
					{editValidationErrors.project && !isDuplicate && (
						<Typography>{editErrorMessages.project}</Typography>
					)}
					{editValidationErrors.sectors && !isDuplicate && (
						<Typography>{editErrorMessages.sectors}</Typography>
					)}
					{editValidationErrors.allocationPercentage && (
						<Typography>{editErrorMessages.allocationPercentage}</Typography>
					)}
					{editValidationErrors.allocationStartTime && <Typography>{editErrorMessages.allocationStartTime}</Typography>}
					{editValidationErrors.allocationEndTime && <Typography>{editErrorMessages.allocationEndTime}</Typography>}
					
					{singleValidationError && (
						<Typography>{'Must select Program/Project/Sector/Environment'}</Typography>
					)}
					{isDuplicate && (
						<Typography>{'Duplicate Program/Project/Sector exist within the same timeline'}</Typography>
					)}
				</Box>
				<Stack
					spacing={12}
					pt={1}
					pb={4}
					marginBottom={1}
					borderBottom={`0.5px solid ${colors.dark.dark}`}
				>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Box display="flex" gap={3}>
							<Stack>
								<Typography variant='bodyText' sx={{ color: `${colors.modal.subTitle} !important` }}>{allocationType.label}</Typography>
								<Typography variant='bodyText'>{currentRow[allocationType.key]}</Typography>
							</Stack>
						</Box>

						<Stack>
							<FormControl
								fullWidth
								variant="outlined"
								sx={{
									minWidth: 400,
									'& .MuiOutlinedInput-root': {
										color: `${colors.input.text} !important`,
										'& fieldset': {
										borderColor: `${colors.input.border} !important`
										},
										'&:hover fieldset': {
										borderColor: `${colors.DropdownV2.borderColorHover} !important`,
										},
										'&.Mui-focused fieldset': {
										borderColor: `${colors.DropdownV2.borderColorHover} !important`,
										},
										'& .MuiSelect-select': {
										color: `${colors.input.text} !important`,
										},
										'& input::placeholder': {
										color: `${colors.input.text} !important`,
										opacity: 1,
										},
									},
									'& .MuiInputLabel-root': {
										color: `${colors.input.text} !important`,
									},
									'& .MuiInputLabel-root.Mui-focused': {
										color: `${colors.input.text} !important`
									},
									'& .MuiInputLabel-root.MuiInputLabel-shrink': { color: `${colors.input.text} !important` }, 
									}}
							>
								<InputLabel id="environment-label">Select Environment</InputLabel>
								<Select
									labelId="environment-label"
									id="environment-select"
									// value={newAllocation.environment}
									value={environment}
									error={validationErrors.environment}
									displayEmpty 
									sx={{
										fontSize: '14px',
										fontWeight: 500,
										padding: '6px',
										'& .MuiOutlinedInput-notchedOutline': {
										borderColor: `${colors.input.border} !important`
										},
										'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: `${colors.DropdownV2.borderColorHover} !important`,
										},
										'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
										borderColor: `${colors.DropdownV2.borderColorHover} !important`,
										},
										'& .MuiSelect-select:empty': {
										color: `${colors.input.text} !important`,
										},
										'& .MuiSelect-icon': {
										color: `${colors.DropdownV2.dropdownIconDefault} !important`, // dropdown arrow color
										},
									}}
									MenuProps={{
										PaperProps: {
										sx: {
											borderRadius: '4px',
											backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
											color: `${colors.DropdownV2.menuItemText} !important`,
										},
										},
										MenuListProps: {
										sx: {
											paddingTop: '0 !important',
											paddingBottom: '0 !important',
										},
										},
									}}
									onChange={(e) => {
										setEnvironment(e.target.value);
										handleChange('PepwiseEnvironment', e.target.value);
									}}
									label="Select Environment"
									IconComponent={KeyboardArrowDownIcon}
								>
									{environmentOptions.map((env) => (
										<MenuItem key={env.value} value={env.value}
											sx={{
												fontSize: '14px !important',
												fontFamily: 'Montserrat !important',
												color: `${colors.DropdownV2.menuItemText} !important`,
												backgroundColor: 'transparent !important',
												'&:hover': {
													backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
													color: `${colors.DropdownV2.menuItemText} !important`,
												},
												'&.Mui-focused': {
													backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
													color: `${colors.DropdownV2.menuItemText} !important`,
												},
												'&.Mui-selected': {
													backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
													color: `${colors.DropdownV2.menuItemText} !important`,
													fontWeight: '500 !important',
												},
												'&.Mui-selected:hover': {
													backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
													color: `${colors.DropdownV2.menuItemText} !important`,
												},
												'&.Mui-selected.Mui-focusVisible': {
													backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
													color: `${colors.DropdownV2.menuItemText} !important`,
												},
											}}
										>
											{env.label}
										</MenuItem>
									))}
								</Select>
								{validationErrors.environment && (
									<FormHelperText sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5, color: 'red' }}>
										Environment is required
									</FormHelperText>
								)}
							</FormControl>
						</Stack>
					</Stack>
					<Stack direction="row" spacing={3} pt={1} justifyContent="space-between" alignItems="center" sx={{marginTop: '0px !important'}}>
						<Box display="flex" gap={3}>
							<Stack>
								<Typography variant='bodyText' sx={{ color: `${colors.modal.subTitle} !important` }}>Program</Typography>
								<Typography variant='bodyText'>{currentRow.Program}</Typography>
							</Stack>
							<Stack>
								<Typography variant='bodyText' sx={{ color: `${colors.modal.subTitle} !important` }}>Project</Typography>
								<Typography variant='bodyText'>{currentRow.ProjectName}</Typography>
							</Stack>
							<Stack>
								<Typography variant='bodyText' sx={{ color: `${colors.modal.subTitle} !important` }}>Sector</Typography>
								<Typography variant='bodyText'>{currentRow.sector}</Typography>
							</Stack>
							<Stack>
								<Typography variant='bodyText' sx={{ color: `${colors.modal.subTitle} !important` }}>Environment</Typography>
								<Typography variant='bodyText'>{currentRow.Environment}</Typography>
							</Stack>
						</Box>
					</Stack>
				</Stack>

				<CustomizedDataGrid
					title="Allocation"
					gridRef={gridRef}
					tableColumns={getColumns()}
					tableData={selectedRows}
					defaultColumnNames={defaultColumnNames}
					autoSetDraggedColumns={false}
					isRenderPagination={false}
					isShowMoreButton={false}
					style={{ maxHeight: '250px' }}
					rowHeight={(row: any) => {
						return row.row.isNew ? 110 : 40;
					}}
					isAllocationModal
				/>
				<Stack>
					<Button
						variant="pepwiseButtonText"
						sx={{ width: '8rem', color: 'text.focus' }}
						onClick={handleAddNewRow}
						disabled={isNewAllocRowExists}
					>
						+ Add Row
					</Button>
				</Stack>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Alert severity="info" sx={{ backgroundColor: `${colors.modal.bg} !important`, color: `${colors.modal.text} !important` }}>
						<Typography sx={{ fontSize: '14px' }}>
							To make sure the resource is fully utilized and saved, please allocate it 100%. This will help us
						</Typography>
						<Typography sx={{ fontSize: '14px' }}>
							optimize our resources and avoid any potential issues.
						</Typography>
					</Alert>
				</Stack>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Button
							variant="pepwiseSecondaryOutline2"
							sx={{ color: 'info.main', width: '12rem', textTransform: 'capitalize !important'}}
							onClick={handleClearAllocationsClick}
							disabled={(selectedRows.length === 1 && selectedRows[0].isNew) || (hasChanges())}
					>
						Clear All Allocations
					</Button>
					<Stack direction="row" gap={2} sx={{ alignSelf: 'flex-end' }} ml='auto'>
						<Button variant="pepwiseOutline" sx={{ width: '8rem', color: `${colors.buttons.primary.color} !important` }} onClick={handleCancel}>
							CANCEL
						</Button>
						<Button variant="pepwiseSecondary" sx={{ width: '8rem' }} onClick={handleConfirm} disabled={ isNewAllocRowExists || !hasChanges()}>
							ALLOCATE
						</Button>
					</Stack>
				</Stack>
			</Stack>
		</Stack>
	) : (
		<ResourceAllocationSuccessModal
		onTitleChange={onTitleChange}
		onWidthChange={onWidthChange}
		onCancel={() => {
			onConfirm();
			onCancel();
		}}
		setIsSuccessModalOpen={setIsSuccessModalOpen}
		modalTitle={modalTitle}
		allocationResponse={response}
		errorResponse={errorRes}
		allocationType={allocationType}
	/>
	)
};

export default ResourceAllocationModal;
